/**
 * ManagePortfolioSkeleton — Manage Portfolio while an opened portfolio
 * loads: grey shimmering placeholders shaped like the page (the Portfolio
 * details card, the Add instance card, the tabs and the instance table), the
 * same behaviour as the Instance Advice page's skeleton.
 *
 * Before, the page kept showing the previous cards (empty fields, a form that
 * could be typed into) with only the table shimmering, so it was unclear
 * that the whole page was about to change. Screen readers hear a sentence.
 *
 * Shaped per product so nothing jumps when the page arrives:
 *  - `tabs` (default true): EIA's Instance Stats / Self Perf tabs with the
 *    safety-margin slider. CCA has none (false) — its table sits right under
 *    the cards.
 *  - `addInstance` (default true): the Add instance card. CCA's Billing (CUR)
 *    mode has none (false), as legacy hides the form there.
 */
import { SkeletonBlock } from '@components/common/Skeleton';

import styles from './ManagePortfolioSkeleton.module.css';

export const LOADING_PORTFOLIO = 'Loading the portfolio, please wait...';

export function ManagePortfolioSkeleton({ tabs = true, addInstance = true }) {
  return (
    <div className={styles.skeleton} role="status" aria-busy="true">
      <span className="sr-only">{LOADING_PORTFOLIO}</span>

      {/* Portfolio details: title, description, then name + the buttons. */}
      <div className={styles.card} data-skeleton="details">
        <SkeletonBlock className={styles.title} />
        <SkeletonBlock className={styles.subtitle} />
        <div className={styles.row}>
          <SkeletonBlock className={styles.nameField} />
          <div className={styles.buttons}>
            {[0, 1, 2].map((i) => (
              <SkeletonBlock key={i} className={styles.button} />
            ))}
          </div>
        </div>
      </div>

      {/* Add instance: title, description, the form grid, the Add button. */}
      {addInstance ? (
        <div className={styles.card} data-skeleton="add-instance">
          <SkeletonBlock className={styles.title} />
          <SkeletonBlock className={styles.subtitle} />
          <div className={styles.grid}>
            {Array.from({ length: 10 }, (_, i) => (
              <SkeletonBlock key={i} className={styles.field} />
            ))}
          </div>
          <SkeletonBlock className={styles.button} />
        </div>
      ) : null}

      {/* EIA: the tabs with the safety margin, above the instance table. */}
      {tabs ? (
        <div className={styles.tabsRow} data-skeleton="tabs">
          <div className={styles.tabs}>
            <SkeletonBlock className={styles.tab} />
            <SkeletonBlock className={styles.tab} />
          </div>
          <SkeletonBlock className={styles.slider} />
        </div>
      ) : null}
      <div className={styles.table} data-skeleton="table">
        <SkeletonBlock className={styles.tableHead} />
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <SkeletonBlock key={i} className={styles.tableRow} />
        ))}
      </div>
    </div>
  );
}

export default ManagePortfolioSkeleton;
